/**
 * Utility functions for formatting and sorting events in selects and lists.
 */

/**
 * Format a short date string like "Mar 5" or "Mar 5, 2026" (if not current year).
 * Accepts an optional locale string to match the app's i18n language.
 */
export function formatShortDate(startTime: string, locale?: string): string {
  const date = new Date(startTime);
  const now = new Date();
  const month = date.toLocaleString(locale || 'default', { month: 'short' });
  const day = date.getDate();

  if (date.getFullYear() !== now.getFullYear()) {
    return `${month} ${day}, ${date.getFullYear()}`;
  }
  return `${month} ${day}`;
}

/**
 * Format an event name with its date for display in selects/dropdowns.
 * Shows "Event Name (Mar 5)" or "Event Name (Mar 5, 2026)" if not current year.
 */
export function formatEventOption(event: { name: string; startTime: string }, locale?: string): string {
  return `${event.name} (${formatShortDate(event.startTime, locale)})`;
}

/**
 * Sort events by date: upcoming first (nearest → furthest), then past (most recent → oldest).
 * Returns a new sorted array.
 */
export function sortEventsByDate<T extends { startTime: string }>(events: T[]): T[] {
  const now = new Date().getTime();
  return [...events].sort((a, b) => {
    const aTime = new Date(a.startTime).getTime();
    const bTime = new Date(b.startTime).getTime();
    const aUpcoming = aTime >= now;
    const bUpcoming = bTime >= now;

    // Upcoming events come first
    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;

    // Both upcoming: nearest first
    if (aUpcoming && bUpcoming) return aTime - bTime;

    // Both past: most recent first
    return bTime - aTime;
  });
}

/**
 * Re-point a held selection at its fresh copy after a refetch. Returns the old
 * object when the row is gone, so an open modal does not blank out.
 */
export function reselectById<T extends { _id: string }>(
  current: T | null,
  fresh: T[],
): T | null {
  if (!current) return null;
  return fresh.find((item) => item._id === current._id) ?? current;
}
